import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

/** Stable per-device id (anti account-sharing + guest usage tracking). */
export function getDeviceId() {
  let id = localStorage.getItem("jv_device_id");
  if (!id) {
    id = (crypto?.randomUUID?.() || `dev_${Date.now()}_${Math.random().toString(36).slice(2)}`);
    localStorage.setItem("jv_device_id", id);
  }
  return id;
}

const api = axios.create({
  baseURL: `${BACKEND_URL}/api`,
  withCredentials: true,
});

// Attach device id to every request
api.interceptors.request.use((config) => {
  config.headers = config.headers || {};
  config.headers["X-Device-Id"] = getDeviceId();
  return config;
});

// Global handler: if the session was ended on another device, sign out + notify
api.interceptors.response.use(
  (res) => res,
  (err) => {
    const detail = err?.response?.data?.detail;
    if (err?.response?.status === 401 && detail && typeof detail === "object" && detail.reason === "device_conflict") {
      if (!window.__jvDeviceConflict) {
        window.__jvDeviceConflict = true;
        try { window.dispatchEvent(new CustomEvent("jv-device-conflict", { detail: detail.message })); } catch (_) {}
      }
    }
    return Promise.reject(err);
  }
);

/** Turn a relative backend path (e.g. /api/files/xxx) into an absolute URL. */
export function fileUrl(path) {
  if (!path) return "";
  if (/^https?:\/\//i.test(path)) return path;
  return `${BACKEND_URL}${path.startsWith("/") ? "" : "/"}${path}`;
}

export function formatApiErrorDetail(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail
      .map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e)))
      .filter(Boolean)
      .join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

export default api;

/**
 * POST form fields to a streaming SSE endpoint and dispatch parsed events.
 * fields: plain object of form values. onEvent(evt) receives {type, ...}.
 * Throws { status, detail } on non-2xx (so callers can handle 402 limits).
 */
export async function streamPost(path, fields, onEvent) {
  const fd = new FormData();
  Object.entries(fields || {}).forEach(([k, v]) => {
    if (v !== undefined && v !== null) fd.append(k, v);
  });
  const res = await fetch(`${BACKEND_URL}/api${path}`, {
    method: "POST",
    credentials: "include",
    headers: { "X-Device-Id": getDeviceId() },
    body: fd,
  });
  if (!res.ok || !res.body) {
    let detail = null;
    try { detail = (await res.json()).detail; } catch (_) { /* ignore */ }
    const err = new Error("stream request failed");
    err.response = { status: res.status, data: { detail } };
    throw err;
  }
  const reader = res.body.getReader();
  const decoder = new TextDecoder();
  let buffer = "";
  while (true) {
    const { done, value } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });
    let idx;
    while ((idx = buffer.indexOf("\n\n")) !== -1) {
      const raw = buffer.slice(0, idx).trim();
      buffer = buffer.slice(idx + 2);
      if (!raw.startsWith("data:")) continue;
      const payload = raw.slice(5).trim();
      if (!payload) continue;
      try { onEvent(JSON.parse(payload)); } catch (_) { /* ignore malformed */ }
    }
  }
}